/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import { Link, ResponsiveHeroImage, ResponsiveImage } from "@quintype/components";
import get from "lodash.get";
import PropTypes from "prop-types";
import React from "react";
import { clientWidth, isExternalStory, getStoryUrl } from "../../../utils/utils";
import { useStateValue } from "../../SharedContext";
import { FallbackImage } from "../FallbackImage";
import "./hero-image.m.css";

export const HeroImage = ({
  story,
  FullBleed,
  aspectRatio,
  defaultWidth,
  widths,
  sizes,
  isHorizontal,
  isHorizontalMobile,
  defaultFallback,
  isStoryPageImage
}) => {
  const { fallbackImageUrl = "" } = useStateValue() || {};

  const fullBleed = FullBleed ? "" : "with-padding";
  const heroImageClasses = isHorizontal ? "right-padding" : "hero-image";
  const heroImageMobClasses = isHorizontalMobile ? "right-padding-mob" : "";

  const getPadding = aspectRatio => {
    const [height, width] = aspectRatio;
    if (height > 0 && width > 0) {
      const padding = (width / height) * 100;
      return padding;
    }
    return 0;
  };

  const [mobileAspectRatio, desktopAspectRatio = mobileAspectRatio] = aspectRatio;
  const isMobile = clientWidth("mobile");

  const imageAspectRatio = isMobile ? mobileAspectRatio : desktopAspectRatio;
  const imagePadding = isMobile ? getPadding(mobileAspectRatio) : getPadding(desktopAspectRatio);

  const slug = get(story, ["hero-image-s3-key"], "");
  const fallbackImage = () => {
    if (!slug) {
      if (fallbackImageUrl) {
        return (
          <figure styleName="image" className="arr--responsive-hero-image" style={{ paddingTop: imagePadding + `%` }}>
            <ResponsiveImage
              slug={!isStoryPageImage && fallbackImageUrl}
              metadata={{}}
              aspect-ratio={imageAspectRatio}
              defaultWidth={defaultWidth}
              widths={widths}
              sizes={sizes}
              imgParams={{ auto: ["format", "compress"], fit: "max" }}
              className=""
              alt={"image-fallback"}
            />
          </figure>
        );
      }

      return (
        <>
          {defaultFallback ? (
            <div styleName="image" className="arr--fallback-hero-image" style={{ paddingTop: imagePadding + `%` }}>
              <FallbackImage />
            </div>
          ) : null}
        </>
      );
    }
    return (
      <figure styleName="image" className="arr--responsive-hero-image" style={{ paddingTop: imagePadding + `%` }}>
        <ResponsiveHeroImage
          story={story}
          aspectRatio={imageAspectRatio}
          defaultWidth={defaultWidth}
          widths={widths}
          sizes={sizes}
          imgParams={{ auto: ["format", "compress"], fit: "max" }}
        />
      </figure>
    );
  };

  return (
    story &&
    (isStoryPageImage ? (
      <div className="arr--hero-image" styleName={`${heroImageClasses} ${fullBleed} ${heroImageMobClasses}`}>
        {fallbackImage()}
      </div>
    ) : (
      <Link
        className="arr--hero-image"
        href={getStoryUrl(story, `/${story.slug}`)}
        externalLink={isExternalStory(story)}
        styleName={`${heroImageClasses} ${fullBleed} ${heroImageMobClasses}`}
      >
        {fallbackImage()}
      </Link>
    ))
  );
};

HeroImage.propTypes = {
  /** The Story Object from the API response */
  story: PropTypes.object.isRequired,
  /** Removes the Hero Image padding for the image to bleed over the margin */
  FullBleed: PropTypes.bool,
  /** The default width for the image, in case the browser does not support srcset */
  defaultWidth: PropTypes.number,
  /** The list of available widths */
  widths: PropTypes.array,
  // fatch the correct hero image
  aspectRatio: PropTypes.array,

  //  Hints for the browser to choose the best size
  //  [see <a target="_blank" href="https://developer.mozilla.org/en-US/docs/Learn/HTML/Multimedia_and_embedding/Responsive_images#Resolution_switching_Different_sizes">syntax</a>]
  sizes: PropTypes.string,
  /** This prop is responsible for adding padding-right of the image and remove padding bottom of the image  */
  isHorizontal: PropTypes.bool,
  /** This prop is responsible for adding padding-bottom in desktop and padding-right in mobile */
  isHorizontalMobile: PropTypes.bool,
  // this prop is responsive for rendering fallback image on story page
  defaultFallback: PropTypes.bool
};

HeroImage.defaultProps = {
  FullBleed: true,
  aspectRatio: [[1, 1], [16, 9]],
  defaultWidth: 480,
  widths: [250, 480, 640],
  sizes: "(max-width: 500px) 98vw, (max-width: 768px) 48vw, 23vw",
  isHorizontal: false,
  isHorizontalMobile: false,
  defaultFallback: true,
  isStoryPageImage: false
};
